import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Loader2, ChevronDown, Check, AlertCircle, Info, Copy, HelpCircle, Gauge, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { showToast } from '@/services/toast.service';
import TenantConfigService, {
  SnowflakeDetails,
  isValidSnowflakeURL,
} from '@/services/tenantConfig.service';
import SnowflakeSsoCard from './SnowflakeSsoCard';
import { useFeatureFlag } from '@/services/featureFlag.service';

const FIND_HOST_SQL = `USE ROLE ACCOUNTADMIN;
SELECT 'https://' || LOWER(CURRENT_ORGANIZATION_NAME() || '-' || CURRENT_ACCOUNT_NAME()) || '.snowflakecomputing.com';`;

const DEFAULT_SNOWFLAKE_DETAILS: SnowflakeDetails = {
  auth_method: 'KEYPAIR',
  private_key: '',
  private_key_passphrase: '',
  host: '',
  username: 'PLIABLE_USER',
  password: '',
  database: 'PLIABLE_DATABASE',
  role: 'PLIABLE_ROLE',
  query_warehouse: 'PLIABLE_QUERY_WAREHOUSE',
  loading_warehouse: 'PLIABLE_LOADING_WAREHOUSE',
  build_warehouse: 'PLIABLE_BUILD_WAREHOUSE',
  loader_username: 'PLIABLE_LOADER_USER',
  loader_database: 'PLIABLE_LOADER_DATABASE',
  loader_role: 'PLIABLE_LOADER_ROLE',
};

export default function SnowflakeConnectionTab() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const snowflakeSsoEnabled = useFeatureFlag('snowflake-sso', false);
  const meteringEnabled = useFeatureFlag('cost-breakdowns', false);
  const [snowflakeDetails, setSnowflakeDetails] = useState<SnowflakeDetails>(
    DEFAULT_SNOWFLAKE_DETAILS
  );
  const [tenantConfigId, setTenantConfigId] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showScriptModal, setShowScriptModal] = useState(false);
  const [showHelpSheet, setShowHelpSheet] = useState(false);
  const [setupSql, setSetupSql] = useState('');

  // Loading states
  const [savingConfig, setSavingConfig] = useState(false);
  const [generatingScript, setGeneratingScript] = useState(false);
  const [testingCredentials, setTestingCredentials] = useState(false);

  // Validation states
  const [credentialsError, setCredentialsError] = useState('');
  const [credentialsWarning, setCredentialsWarning] = useState('');
  const [connectionValid, setConnectionValid] = useState(false);

  // Pliable-hosted Snowflake, revealed with ctrl+h (mirrors the onboarding wizard in
  // frontend/). Not discoverable on purpose — it's for staff walking a customer through
  // setup, and the backend still requires a valid activation code.
  const [showHostedForm, setShowHostedForm] = useState(false);
  const [activationCode, setActivationCode] = useState('');
  const [creatingHostedSnowflake, setCreatingHostedSnowflake] = useState(false);
  const [hostedError, setHostedError] = useState('');

  // Fetch tenant registration data
  const { data: registrationData, isLoading } = useQuery({
    queryKey: ['tenantRegistration'],
    queryFn: async () => {
      return await TenantConfigService.getInstance().getTenantRegistration();
    },
    staleTime: 5 * 60 * 1000,
  });

  // Only tenants already saved with password auth keep access to the legacy option.
  const hasLegacyPasswordAuth =
    registrationData?.connection_creds?.auth_method === 'PASSWORD';

  // Initialize form with fetched data
  useEffect(() => {
    if (registrationData) {
      const { tenant_config, connection_creds, loader_creds } = registrationData;
      setTenantConfigId(tenant_config.id);
      setConnectionValid(tenant_config.infra.is_ready);

      setSnowflakeDetails({
        auth_method: (connection_creds.auth_method as 'PASSWORD' | 'KEYPAIR') || 'KEYPAIR',
        private_key: connection_creds.private_key || '',
        private_key_passphrase: connection_creds.private_key_passphrase || '',
        host: connection_creds.host || '',
        username: connection_creds.username || 'PLIABLE_USER',
        password: connection_creds.password || '',
        database: connection_creds.database || 'PLIABLE_DATABASE',
        role: connection_creds.role || 'PLIABLE_ROLE',
        query_warehouse: connection_creds.query_warehouse || 'PLIABLE_QUERY_WAREHOUSE',
        build_warehouse: connection_creds.build_warehouse || 'PLIABLE_BUILD_WAREHOUSE',
        loading_warehouse: loader_creds.loading_warehouse || 'PLIABLE_LOADING_WAREHOUSE',
        loader_username: loader_creds.username || 'PLIABLE_LOADER_USER',
        loader_password: loader_creds.password || '',
        loader_private_key: loader_creds.private_key || '',
        loader_private_key_passphrase: loader_creds.private_key_passphrase || '',
        loader_database: loader_creds.database || 'PLIABLE_LOADER_DATABASE',
        loader_role: loader_creds.role || 'PLIABLE_LOADER_ROLE',
      });
    }
  }, [registrationData]);

  const validHost = useMemo(() => {
    return isValidSnowflakeURL(snowflakeDetails.host);
  }, [snowflakeDetails.host]);

  const formIsValid = useMemo(() => {
    return (
      validHost &&
      !!snowflakeDetails.username &&
      !!snowflakeDetails.database &&
      !!snowflakeDetails.role &&
      !!snowflakeDetails.query_warehouse
    );
  }, [snowflakeDetails, validHost]);

  const updateField = useCallback(
    <K extends keyof SnowflakeDetails>(key: K, value: SnowflakeDetails[K]) => {
      setSnowflakeDetails((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const saveSnowflakeDetails = useCallback(
    async (generateScript: boolean = false) => {
      if (!tenantConfigId) return;

      setSavingConfig(true);
      try {
        await TenantConfigService.getInstance().saveSnowflakeDetails(
          tenantConfigId,
          snowflakeDetails
        );

        if (generateScript) {
          setGeneratingScript(true);
          const { snowflake_setup_sql } =
            await TenantConfigService.getInstance().showSetupScript(tenantConfigId);
          setSetupSql(snowflake_setup_sql);
          setShowScriptModal(true);
        } else {
          showToast.success('Snowflake settings saved');
        }
      } catch (err: any) {
        showToast.error('Failed to save settings', {
          description: err.message || 'An error occurred',
        });
      } finally {
        setSavingConfig(false);
        setGeneratingScript(false);
      }
    },
    [snowflakeDetails, tenantConfigId]
  );

  const testCredentials = useCallback(async () => {
    if (!tenantConfigId) return;

    setTestingCredentials(true);
    setConnectionValid(false);
    setCredentialsError('');
    setCredentialsWarning('');

    try {
      // First save the credentials
      await TenantConfigService.getInstance().saveSnowflakeDetails(
        tenantConfigId,
        snowflakeDetails
      );

      // Verify access
      const { has_db_access } =
        await TenantConfigService.getInstance().verifySnowflakeAccess(tenantConfigId);

      if (!has_db_access) {
        throw new Error('Unable to connect to Snowflake.');
      }

      // Initialize infrastructure
      const infra =
        await TenantConfigService.getInstance().initInfrastructure(tenantConfigId);

      if (infra.is_ready) {
        setConnectionValid(true);
        showToast.success('Connection successful!');

        if (infra.snowpipe && !infra.snowpipe.setup_complete) {
          setCredentialsWarning(
            'Your Snowflake account is connected, but we were unable to set up the infrastructure for file uploads. You may need to grant the Pliable user the necessary permissions to create stages and pipes. Error: ' +
              (infra.snowpipe.setup_error || 'Unknown error')
          );
        } else if (infra.snowpipe?.setup_error) {
          setCredentialsWarning(
            'Your Snowflake account is connected successfully. However, we were unable to set up Iceberg external volume support, which is needed for local DuckDB processing. This feature is off by default and won\'t affect normal usage. If you need it, you may need to grant additional permissions. Error: ' +
              infra.snowpipe.setup_error
          );
        }

        // Invalidate queries to refresh data
        queryClient.invalidateQueries({ queryKey: ['tenantRegistration'] });
      } else {
        throw new Error('Error setting up infrastructure.');
      }
    } catch (err: any) {
      setCredentialsError(err.message || 'An error occurred');
      setConnectionValid(false);
    } finally {
      setTestingCredentials(false);
    }
  }, [snowflakeDetails, tenantConfigId, queryClient]);

  const copyHostSql = useCallback(() => {
    navigator.clipboard.writeText(FIND_HOST_SQL);
    showToast.success('SQL copied to clipboard');
  }, []);

  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(setupSql);
    showToast.success('SQL copied to clipboard');
  }, [setupSql]);

  // Ctrl+H toggles the hosted-Snowflake form, but only while the connection is still
  // unvalidated — once infra is ready the backend refuses to provision anyway, so
  // there's nothing to reveal. Skipped while typing so it can't fire from a field.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'h' || !event.ctrlKey || event.metaKey) return;
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }
      if (connectionValid) return;
      event.preventDefault();
      setShowHostedForm((v) => !v);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [connectionValid]);

  const createPliableHostedSnowflake = useCallback(async () => {
    if (!tenantConfigId) return;

    setCreatingHostedSnowflake(true);
    setHostedError('');
    try {
      await TenantConfigService.getInstance().createHostedSnowflake(
        tenantConfigId,
        activationCode
      );
      setConnectionValid(true);
      // Drop back to the account details view, which now renders the validated banner.
      // Keeps the invariant that the hosted form is only ever visible pre-validation.
      setShowHostedForm(false);
      showToast.success('Snowflake account created');
      queryClient.invalidateQueries({ queryKey: ['tenantRegistration'] });
    } catch (err: any) {
      setHostedError(err.message || 'An error occurred');
    } finally {
      setCreatingHostedSnowflake(false);
    }
  }, [activationCode, tenantConfigId, queryClient]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      {/* Setup Script Modal */}
      <Dialog open={showScriptModal} onOpenChange={setShowScriptModal}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Setup Script</DialogTitle>
            <DialogDescription>
              This script will create all of the resources (warehouses, databases, users,
              roles, etc) that Pliable needs to operate in your Snowflake account.
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-4">
            <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950 rounded-md">
              <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-800 dark:text-blue-200">
                Log into your Snowflake account and run the following script in a new
                worksheet. Make sure "All Queries" is checked. When you're done, close this
                window and click on "Validate Connection".
              </p>
            </div>

            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                className="absolute right-2 top-2 z-10"
                onClick={copyToClipboard}
              >
                <Copy className="h-4 w-4 mr-1" />
                Copy
              </Button>
              <pre className="p-4 bg-muted rounded-md overflow-auto max-h-[400px] text-sm font-mono">
                {setupSql}
              </pre>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>

      {/* Help Sheet */}
      <Sheet open={showHelpSheet} onOpenChange={setShowHelpSheet}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Finding Your Snowflake Host</SheetTitle>
            <SheetDescription>
              Follow these steps to find your Snowflake hostname
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-6">
            <div>
              <h4 className="font-medium mb-2">1. Log into your Snowflake account</h4>
            </div>
            <div>
              <h4 className="font-medium mb-2">2. Open up a new SQL worksheet</h4>
              <p className="text-sm text-muted-foreground">
                Go to "Projects" in the sidebar, then click on the arrow next to the blue
                "+" button at the top right of your screen. Select "SQL Worksheet".
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-2">3. Run the SQL command</h4>
              <p className="text-sm text-muted-foreground mb-2">
                In the SQL editor, paste the following command:
              </p>
              <div className="relative">
                <Button
                  variant="outline"
                  size="sm"
                  className="absolute right-2 top-2 z-10"
                  onClick={copyHostSql}
                >
                  <Copy className="h-4 w-4 mr-1" />
                  Copy
                </Button>
                <pre className="p-3 pr-24 bg-muted rounded-md text-sm font-mono overflow-x-auto">
                  {FIND_HOST_SQL}
                </pre>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Click the arrow next to the blue "Run" button at the top right of the
                worksheet and choose "Run All" so both statements execute. The result is
                your Snowflake host.
              </p>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Main Form */}
      <Card>
        <CardHeader>
          <CardTitle>
            {showHostedForm
              ? 'Let Pliable create a Snowflake account for me'
              : 'Snowflake Account Details'}
          </CardTitle>
          <CardDescription>
            {showHostedForm
              ? 'Your Pliable account will incur additional charges based on your usage of Snowflake.'
              : "Configure your Snowflake connection. We'll use these details to generate a script for you to run on your Snowflake account, which will create all of the resources Pliable needs to operate."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Connection Status */}
          {connectionValid && (
            <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-950 rounded-md">
              <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
              <span className="text-sm text-green-800 dark:text-green-200">
                Connection is active and validated
              </span>
            </div>
          )}

          {showHostedForm ? (
            /* Pliable-hosted Snowflake — revealed with ctrl+h, replaces the account
               details form entirely so there's no ambiguity about which path is active. */
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="activation_code">Activation Code</Label>
                <Input
                  id="activation_code"
                  type="password"
                  value={activationCode}
                  onChange={(e) => setActivationCode(e.target.value)}
                />
              </div>

              <Button
                onClick={createPliableHostedSnowflake}
                disabled={!activationCode || creatingHostedSnowflake}
              >
                {creatingHostedSnowflake ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Creating...
                  </>
                ) : (
                  'Create Snowflake Account'
                )}
              </Button>

              {creatingHostedSnowflake && (
                <p className="text-sm text-muted-foreground">
                  Setting up a new Snowflake account for you. This may take up to 5
                  minutes.
                </p>
              )}

              {hostedError && (
                <div className="p-3 bg-red-50 dark:bg-red-950 rounded-md">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-800 dark:text-red-200">
                        Unable to create Snowflake account
                      </p>
                      <p className="text-sm text-red-600 dark:text-red-400 mt-2 font-mono">
                        {hostedError}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
          <>
          {/* Host Field */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="host">Host</Label>
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 text-sm text-muted-foreground hover:text-foreground"
                onClick={() => setShowHelpSheet(true)}
              >
                <HelpCircle className="h-4 w-4 mr-1" />
                Where do I find this?
              </Button>
            </div>
            <Input
              id="host"
              type="text"
              placeholder="your-account.snowflakecomputing.com"
              value={snowflakeDetails.host}
              onChange={(e) => updateField('host', e.target.value)}
              className={
                snowflakeDetails.host
                  ? validHost
                    ? 'border-green-500 focus-visible:ring-green-500'
                    : 'border-red-500 focus-visible:ring-red-500'
                  : ''
              }
            />
            <p className="text-sm text-muted-foreground">
              This needs to be the "regionless" hostname in order to work with Snowflake's
              newer features, such as the Cortex API.
            </p>
          </div>

          {/* Advanced Configuration Toggle */}
          <div className="flex items-center space-x-2">
            <Switch
              id="show-advanced"
              checked={showAdvanced}
              onCheckedChange={setShowAdvanced}
            />
            <Label htmlFor="show-advanced">Show Advanced Configuration</Label>
          </div>

          {/* Advanced Configuration */}
          <Collapsible open={showAdvanced}>
            <CollapsibleContent>
              <div className="border rounded-lg p-4 space-y-6 mt-2">
                <h4 className="font-medium">Advanced Configuration</h4>

                {/* Authentication Method — keypair is the only option for new setups. The
                    dropdown only appears for tenants already saved with password auth, so
                    they can migrate to keypair (and back until they save). */}
                {hasLegacyPasswordAuth && (
                  <div className="space-y-2">
                    <Label>Authentication Method</Label>
                    <Select
                      value={snowflakeDetails.auth_method}
                      onValueChange={(val: 'PASSWORD' | 'KEYPAIR') =>
                        updateField('auth_method', val)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PASSWORD">Username/Password (legacy)</SelectItem>
                        <SelectItem value="KEYPAIR">Keypair</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-muted-foreground">
                      Snowflake no longer supports password authentication. Switch to
                      keypair to keep your connection working.
                    </p>
                  </div>
                )}

                {/* Build Credentials Section */}
                <div className="space-y-4">
                  <div>
                    <h5 className="font-medium">Build Credentials</h5>
                    <p className="text-sm text-muted-foreground">
                      These credentials are used for building your models and running
                      queries. It's best practice to keep these separate from your loader
                      credentials.
                    </p>
                  </div>

                  <div className="grid gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="username">Username</Label>
                      <Input
                        id="username"
                        value={snowflakeDetails.username}
                        onChange={(e) => updateField('username', e.target.value)}
                      />
                    </div>

                    {snowflakeDetails.auth_method === 'KEYPAIR' ? (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="private_key">Private Key</Label>
                          <Textarea
                            id="private_key"
                            value={snowflakeDetails.private_key}
                            onChange={(e) => updateField('private_key', e.target.value)}
                            placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
                            rows={4}
                          />
                          <p className="text-sm text-muted-foreground">
                            Paste the entire private key here, including the BEGIN/END
                            markers.{' '}
                            <a
                              href="https://docs.snowflake.com/en/user-guide/key-pair-auth"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline"
                            >
                              Learn more about key-pair auth
                            </a>
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="private_key_passphrase">
                            Private Key Passphrase (Optional)
                          </Label>
                          <Input
                            id="private_key_passphrase"
                            type="password"
                            value={snowflakeDetails.private_key_passphrase}
                            onChange={(e) =>
                              updateField('private_key_passphrase', e.target.value)
                            }
                          />
                        </div>
                      </>
                    ) : (
                      <div className="space-y-2">
                        <Label htmlFor="password">Password</Label>
                        <Input
                          id="password"
                          type="password"
                          value={snowflakeDetails.password}
                          onChange={(e) => updateField('password', e.target.value)}
                        />
                        <p className="text-sm text-muted-foreground">
                          Leave blank and we will automatically generate one for you.
                        </p>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="database">Database</Label>
                      <Input
                        id="database"
                        value={snowflakeDetails.database}
                        onChange={(e) => updateField('database', e.target.value)}
                      />
                      <p className="text-sm text-muted-foreground">
                        Enter the name of the database where Pliable should store its data
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="build_warehouse">Warehouse (Building)</Label>
                        <Input
                          id="build_warehouse"
                          value={snowflakeDetails.build_warehouse}
                          onChange={(e) => updateField('build_warehouse', e.target.value)}
                        />
                        <p className="text-sm text-muted-foreground">
                          For building models
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="query_warehouse">Warehouse (Querying)</Label>
                        <Input
                          id="query_warehouse"
                          value={snowflakeDetails.query_warehouse}
                          onChange={(e) => updateField('query_warehouse', e.target.value)}
                        />
                        <p className="text-sm text-muted-foreground">For running queries</p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="role">Role</Label>
                      <Input
                        id="role"
                        value={snowflakeDetails.role}
                        onChange={(e) => updateField('role', e.target.value)}
                      />
                      <p className="text-sm text-muted-foreground">
                        The role Pliable should use when interacting with Snowflake
                      </p>
                    </div>
                  </div>
                </div>

                {/* Loading Credentials Section */}
                <div className="space-y-4 border-t pt-4">
                  <div>
                    <h5 className="font-medium">Loading Credentials (Optional)</h5>
                    <p className="text-sm text-muted-foreground">
                      If you are using Pliable's managed ETL functionality, you will also
                      need to provide credentials for loading data into Snowflake.
                    </p>
                  </div>

                  <div className="grid gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="loader_username">User</Label>
                      <Input
                        id="loader_username"
                        value={snowflakeDetails.loader_username}
                        onChange={(e) => updateField('loader_username', e.target.value)}
                      />
                    </div>

                    {snowflakeDetails.auth_method === 'KEYPAIR' ? (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="loader_private_key">Private Key</Label>
                          <Textarea
                            id="loader_private_key"
                            value={snowflakeDetails.loader_private_key}
                            onChange={(e) =>
                              updateField('loader_private_key', e.target.value)
                            }
                            rows={4}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="loader_private_key_passphrase">
                            Private Key Passphrase (Optional)
                          </Label>
                          <Input
                            id="loader_private_key_passphrase"
                            type="password"
                            value={snowflakeDetails.loader_private_key_passphrase}
                            onChange={(e) =>
                              updateField('loader_private_key_passphrase', e.target.value)
                            }
                          />
                        </div>
                      </>
                    ) : (
                      <div className="space-y-2">
                        <Label htmlFor="loader_password">Password</Label>
                        <Input
                          id="loader_password"
                          type="password"
                          value={snowflakeDetails.loader_password}
                          onChange={(e) => updateField('loader_password', e.target.value)}
                        />
                        <p className="text-sm text-muted-foreground">
                          Leave blank and we will automatically generate one for you.
                        </p>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="loader_database">Database</Label>
                      <Input
                        id="loader_database"
                        value={snowflakeDetails.loader_database}
                        onChange={(e) => updateField('loader_database', e.target.value)}
                      />
                      <p className="text-sm text-muted-foreground">
                        For housing raw data ingested from your data sources
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="loader_role">Role</Label>
                        <Input
                          id="loader_role"
                          value={snowflakeDetails.loader_role}
                          onChange={(e) => updateField('loader_role', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="loading_warehouse">Warehouse (Loading)</Label>
                        <Input
                          id="loading_warehouse"
                          value={snowflakeDetails.loading_warehouse}
                          onChange={(e) =>
                            updateField('loading_warehouse', e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Action Buttons */}
          <div className="space-y-4 pt-4 border-t">
            <div>
              <p className="text-sm text-muted-foreground mb-3">
                Once you're ready, generate and run the setup script to connect Pliable to
                your Snowflake.
              </p>
              <Button
                onClick={() => saveSnowflakeDetails(true)}
                disabled={!formIsValid || savingConfig || generatingScript}
              >
                {generatingScript ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Generating...
                  </>
                ) : (
                  'Generate Setup Script'
                )}
              </Button>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-1">Validate Connection</h4>
              <p className="text-sm text-muted-foreground mb-3">
                Once you've run the script, validate the connection.
              </p>
              <Button
                onClick={testCredentials}
                disabled={!formIsValid || testingCredentials}
              >
                {testingCredentials ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Validating...
                  </>
                ) : (
                  'Validate Connection'
                )}
              </Button>

              {testingCredentials && (
                <p className="text-sm text-muted-foreground mt-2">
                  We're running a few different checks, which may take up to 30 seconds.
                </p>
              )}

              {credentialsError && (
                <div className="mt-3 p-3 bg-red-50 dark:bg-red-950 rounded-md">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-800 dark:text-red-200">
                        Connection Failed
                      </p>
                      <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                        We were unable to connect to your Snowflake. Please check your
                        credentials and make sure the setup script ran successfully.
                      </p>
                      <p className="text-sm text-red-600 dark:text-red-400 mt-2 font-mono">
                        {credentialsError}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {credentialsWarning && (
                <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-950 rounded-md overflow-hidden">
                  <div className="flex items-start gap-2 min-w-0">
                    <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-yellow-800 dark:text-yellow-200 break-words min-w-0">
                      {credentialsWarning}
                    </p>
                  </div>
                </div>
              )}

              {connectionValid && !testingCredentials && !credentialsError && (
                <div className="mt-3 p-3 bg-green-50 dark:bg-green-950 rounded-md">
                  <div className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
                    <span className="text-sm text-green-800 dark:text-green-200">
                      Connection successful!
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
          </>
          )}
        </CardContent>
      </Card>

      {tenantConfigId && snowflakeSsoEnabled && (
        <SnowflakeSsoCard tenantConfigId={tenantConfigId} />
      )}

      {meteringEnabled && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gauge className="h-4 w-4" />
              LLM Metering has moved
            </CardTitle>
            <CardDescription>
              Per-thread token and warehouse-compute cost for your AI chats now lives in
              Usage Analytics, on the Ellie Usage tab.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => navigate('/usage-analytics/ellie-usage')}>
              Go to Usage Analytics
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
